import { groqService } from './GroqService'; // Groq AI - Miễn phí & Cực nhanh
import {
  apiGetConsumptionMonitoring,
  apiGetStatisticsDataByMonth,
  apiGetStatisticsCustomerByDay,
  apiGetSumConsumptionByMonth,
  apiGetSumConsumptionByDay,
  apiGetAllCustomer,
  apiGetConsumptionByBranchByDay,
  apiGetStatisticsDataByMonthV3
} from '@services/ManageCustomers/CustomerServices';
import {
  apiGetAllWarnings,
  apiGetWarningStatisticsByType
} from '@services/ManageDevices/DeviceServices';
import { formatCurrency } from '@utils/utils';

/**
 * Service xử lý chatbot queries và tích hợp với backend API
 */
class ChatBotService {
  constructor() {
    // Danh sách Nhà máy với mã
    this.branches = [
      {
        name: 'Nhà máy cấp nước Trung tâm',
        code: '1',
        aliases: ['trung tâm', 'trung tam', 'ttâm', 'tt']
      },
      {
        name: 'Nhà máy cấp nước Hải Phòng 7',
        code: '7',
        aliases: ['hải phòng 7', 'hp7', 'hp 7']
      },
      {
        name: 'Nhà máy cấp nước Hải Phòng 5',
        code: '5',
        aliases: ['hải phòng 5', 'hp5', 'hp 5']
      },
      {
        name: 'Nhà máy cấp nước Hải Phòng 8',
        code: '14',
        aliases: ['hải phòng 8', 'hp8', 'hp 8']
      },
      {
        name: 'Nhà máy cấp nước Hải Phòng 3',
        code: '12',
        aliases: ['hải phòng 3', 'hp3', 'hp 3']
      },
      {
        name: 'Nhà máy cấp nước Hải Phòng 4',
        code: '4',
        aliases: ['hải phòng 4', 'hp4', 'hp 4']
      },
      { name: 'Nhà máy cấp nước Vĩnh Bảo', code: '9', aliases: ['vĩnh bảo', 'vinh bao', 'vbao'] },
      { name: 'Nhà máy cấp nước Cát Bà', code: '8', aliases: ['cát bà', 'cat ba', 'cb'] }
    ];
  }

  /**
   * Xử lý tin nhắn từ người dùng
   * @param {string} userMessage - Tin nhắn người dùng
   * @param {Array} conversationHistory - Lịch sử hội thoại
   * @returns {Promise<Object>} - Response với message và data
   */
  async sendMessage(userMessage, conversationHistory = []) {
    try {
      // Phân tích intent cục bộ (không gọi AI)
      const intent = this.analyzeIntentLocal(userMessage);

      console.log('Intent detected (Local):', intent);

      // Bước 2: Nếu cần data, gọi API để lấy dữ liệu thực
      let apiData = null;
      let systemContext = '';
      let finalResponse = '';

      if (intent.needsData) {
        apiData = await this.fetchRelevantData(intent, userMessage);

        if (apiData) {
          // Tạo context từ dữ liệu
          systemContext = this.buildContextFromData(apiData, intent);

          // GỌI AI để tạo câu trả lời thông minh dựa trên dữ liệu
          try {
            console.log('🤖 Gọi Groq AI để phân tích dữ liệu...');
            const aiResponse = await groqService.chat(
              userMessage,
              conversationHistory,
              systemContext
            );
            finalResponse = this.formatAIResponse(aiResponse);
          } catch (e) {
            // Nếu AI lỗi, dùng template fallback
            console.warn('⚠️ AI error, dùng template fallback:', e);
            finalResponse = this.generateResponseFromData(userMessage, apiData);
          }
        } else {
          finalResponse = 'Xin lỗi, tôi không tìm thấy dữ liệu phù hợp cho yêu cầu của bạn.';
        }
      } else {
        // Nếu không phải câu hỏi dữ liệu (chitchat), gọi AI trực tiếp
        try {
          const aiResponse = await groqService.chat(userMessage, conversationHistory, '');
          finalResponse = this.formatAIResponse(aiResponse);
        } catch (e) {
          finalResponse = this.getFallbackResponse(e);
        }
      }

      return {
        message: finalResponse,
        data: apiData,
        intent: intent
      };
    } catch (error) {
      console.error('ChatBot Service Error:', error);

      // Fallback response
      return {
        message: this.getFallbackResponse(error),
        data: null,
        error: true
      };
    }
  }

  /**
   * Phân tích intent dựa trên từ khóa (Rule-based)
   * Thay thế cho việc gọi AI để phân tích
   */
  analyzeIntentLocal(message) {
    const lowerMessage = message.toLowerCase();

    // Check for branch names in message
    const branchDetected = this.detectBranchFromMessage(message);

    // Detect query about Monthly Consumption (Tiêu thụ theo tháng/kỳ)
    if (
      (lowerMessage.includes('tiêu thụ') &&
        (lowerMessage.includes('tháng') ||
          lowerMessage.includes('kỳ') ||
          lowerMessage.includes('cước') ||
          lowerMessage.includes('hóa đơn'))) ||
      lowerMessage.includes('chỉ số') ||
      lowerMessage.includes('đồng hồ') ||
      lowerMessage.includes('meter reading')
    ) {
      return {
        intent: 'MONTHLY_CONSUMPTION_QUERY',
        needsData: true
      };
    }

    // Detect query about Branches (Nhà máy)
    // Check for explicit "Nhà máy" OR any branch name mention with "tiêu thụ theo ngày"
    if (
      lowerMessage.includes('Nhà máy') ||
      lowerMessage.includes('branch') ||
      (branchDetected && lowerMessage.includes('tiêu thụ') && lowerMessage.includes('ngày'))
    ) {
      return {
        intent: 'BRANCH_QUERY',
        needsData: true
      };
    }

    // Detect query about Consumption (Tiêu thụ - chung)
    if (
      lowerMessage.includes('tiêu thụ') ||
      lowerMessage.includes('nước') ||
      lowerMessage.includes('consumption')
    ) {
      // Nếu câu hỏi có vẻ hỏi về tổng quát hoặc giám sát
      return {
        intent: 'CONSUMPTION_QUERY',
        needsData: true
      };
    }

    // Detect query about Statistics (Thống kê)
    if (
      lowerMessage.includes('thống kê') ||
      lowerMessage.includes('báo cáo') ||
      lowerMessage.includes('report')
    ) {
      return {
        intent: 'STATISTICS_QUERY',
        needsData: true
      };
    }

    // Detect query about Customers (Khách hàng)
    if (
      lowerMessage.includes('khách hàng') ||
      lowerMessage.includes('customer') ||
      lowerMessage.includes('tìm')
    ) {
      // Extract tên khách hàng đơn giản (sau chữ "tìm" hoặc "khách hàng")
      let entity = '';
      if (lowerMessage.includes('tìm')) {
        entity = message.substring(lowerMessage.indexOf('tìm') + 3).trim();
      }
      return {
        intent: 'CUSTOMER_QUERY',
        needsData: true,
        entity: entity
      };
    }

    // Detect query about Warnings (Cảnh báo)
    if (
      lowerMessage.includes('cảnh báo') ||
      lowerMessage.includes('warning') ||
      lowerMessage.includes('cảnh bảo') ||
      (lowerMessage.includes('tra cứu') && lowerMessage.includes('cảnh'))
    ) {
      return {
        intent: 'WARNING_QUERY',
        needsData: true
      };
    }

    // Mặc định: Chitchat hoặc câu hỏi chung
    return {
      intent: 'GENERAL_QUERY',
      needsData: false
    };
  }

  /**
   * Tạo câu trả lời từ dữ liệu (Template-based response)
   */
  generateResponseFromData(userMessage, apiData) {
    // Tận dụng hàm buildContext cũ nhưng bỏ các tiền tố không cần thiết nếu muốn
    // Hoặc trả về format đẹp hơn

    let response = '';

    switch (apiData.type) {
      case 'branch_consumption':
        response = `📊 **TIÊU THỤ THEO NGÀY - Nhà máy**\n`;
        response += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
        response += `💡 *Loại dữ liệu:* Tiêu thụ hàng ngày của các Nhà máy\n`;
        response += `📅 **Ngày:** **${apiData.date}**\n`;
        response += `📈 **Tổng toàn hệ thống:** **${this.formatNumber(
          apiData.totalConsumption
        )} m³**\n`;
        response += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

        if (apiData.hasSpecificBranch && apiData.requestedBranch) {
          const b = apiData.requestedBranch;
          response += `🏢 **${b.branch_name}**\n`;
          response += `   • Mã: **${b.branch_code}**\n`;
          response += `   • Tiêu thụ: **${this.formatNumber(b.consumption)} m³**\n`;
          const percent = apiData.totalConsumption
            ? ((b.consumption / apiData.totalConsumption) * 100).toFixed(2)
            : 0;
          response += `   • Tỷ trọng: **${percent}%**\n\n`;
        } else {
          response += `📊 **Chi tiết các Nhà máy:**\n\n`;
          apiData.branches.forEach((b, idx) => {
            response += `**${idx + 1}. ${b.branch_name}**\n`;
            response += `   • Tiêu thụ: **${this.formatNumber(b.consumption)} m³**\n\n`;
          });
        }
        response += `━━━━━━━━━━━━━━━━━━━━━━━━━━`;
        break;

      case 'consumption':
        response = `📊 **GIÁM SÁT TIÊU THỤ**\n`;
        response += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
        response += `📅 **Kỳ:** Tháng **${apiData.summary.period}**\n`;
        response += `👥 **Tổng KH:** **${this.formatNumber(apiData.summary.totalCustomers)}**\n`;
        response += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

        if (apiData.monitoring && apiData.monitoring.length > 0) {
          response += `🔺 **Top tiêu thụ cao:**\n\n`;
          apiData.monitoring.slice(0, 5).forEach((item, idx) => {
            response += `**${idx + 1}. ${item.customerName}**\n`;
            response += `   • Tiêu thụ: **${item.consumption} m³**\n\n`;
          });
          response += `━━━━━━━━━━━━━━━━━━━━━━━━━━`;
        }
        break;

      case 'customer':
        response = this.generateCustomerResponse(apiData);
        break;

      case 'monthly_consumption':
        response = this.generateMonthlyConsumptionResponse(apiData);
        break;

      case 'monthly_consumption_no_input':
        response = apiData.message;
        break;

      case 'warning':
      case 'warning_summary':
        response = this.generateWarningResponse(apiData);
        break;

      default:
        response = this.buildContextFromData(apiData, { intent: '' });
    }

    if (apiData.isMock) {
      response += '\n\n⚠️ *Lưu ý: Dữ liệu giả lập (Demo mode)*';
    }

    return response;
  }

  /**
   * Tạo template response cho customer data
   */
  generateCustomerResponse(apiData) {
    let response = `📋 **THÔNG TIN KHÁCH HÀNG**\n`;
    response += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    if (apiData.searchTerm) {
      response += `🔍 **Từ khóa:** "**${apiData.searchTerm}**"\n`;
    }

    response += `📈 **Kết quả:** **${apiData.total}** khách hàng\n`;
    response += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    if (apiData.customers.length > 0) {
      apiData.customers.slice(0, 10).forEach((customer, index) => {
        response += `**${index + 1}. ${customer.name}** · Mã: **${customer.code}**\n`;
        response += `   ☎️ ${customer.phone} · 🏢 ${customer.branch}\n`;
        response += `   📍 ${customer.address}\n`;
        response += `   🔧 ${customer.device_name} (**${customer.size}**) · SN: ${customer.serial_number}\n`;
        const statusText = customer.status === 'IN_USE' ? '🟢 Đang sử dụng' : customer.status;
        response += `   ${statusText}\n`;
        response += `\n───────────────────────────\n\n`;
      });

      if (apiData.total > 10) {
        response += `📦 **+${apiData.total - 10}** khách hàng khác...\n\n`;
      }
    } else {
      response += '❌ **Không tìm thấy khách hàng nào phù hợp.**\n\n';
    }

    response += `━━━━━━━━━━━━━━━━━━━━━━━━━━`;

    if (apiData.isMock) {
      response += '\n\n⚠️ *Dữ liệu mô phỏng (Demo mode)*';
    }

    return response;
  }

  /**
   * Tạo template response cho monthly consumption data
   */
  generateMonthlyConsumptionResponse(apiData) {
    const periodDate = new Date(apiData.period);
    const periodText = `${String(periodDate.getMonth() + 1).padStart(
      2,
      '0'
    )}/${periodDate.getFullYear()}`;

    let response = `📊 **TIÊU THỤ THEO KỲ CƯỚC**\n`;
    response += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    response += `💡 *Loại dữ liệu:* Tiêu thụ theo kỳ cước/tháng của khách hàng\n`;
    response += `📅 **Kỳ cước:** **${periodText}**\n`;

    if (apiData.branchInfo) {
      response += `🏢 **Nhà máy:** **${apiData.branchInfo.name}**\n`;
    }

    if (apiData.searchTerm) {
      response += `🔍 **Từ khóa:** "**${apiData.searchTerm}**"\n`;
    }

    response += `📈 **Kết quả:** **${apiData.total}** bản ghi\n`;
    response += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    if (apiData.records.length > 0) {
      apiData.records.slice(0, 10).forEach((record, index) => {
        response += `**${index + 1}. ${record.customer_name}** · Mã: **${record.customer_id}**\n`;
        response += `   🏢 ${record.branch} · 🔧 Cỡ: **${record.size}**\n`;
        response += `   📅 Kỳ: ${record.first_day} → ${record.last_day}\n\n`;

        // Chỉ số
        response += `   **📊 Chỉ số:**\n`;
        response += `      • Cũ: ${this.formatNumber(record.start_reading || 0)} m³\n`;
        response += `      • Mới: ${this.formatNumber(record.end_reading || 0)} m³\n\n`;

        // Tiêu thụ
        response += `   **💧 Tiêu thụ:**\n`;
        response += `      • Hệ thống: **${this.formatNumber(record.consumption || 0)} m³**\n`;

        if (record.adjusted_consumption_value && record.adjusted_consumption_value !== 0) {
          response += `      • Điều chỉnh: ${this.formatNumber(
            record.adjusted_consumption_value
          )} m³\n`;
        }

        response += `      • Tính cước: **${this.formatNumber(
          record.final_consumption || 0
        )} m³**\n\n`;

        // Trạng thái
        let statusText = '🟢 Đã tính toán';
        if (record.status === 0) statusText = '🟡 Chưa gán';
        else if (record.status === 1) statusText = '🔴 Chưa đủ dữ liệu';
        else if (record.status === 3) statusText = '🔵 Đọc thủ công';
        response += `   ${statusText}\n`;
        response += `\n───────────────────────────\n\n`;
      });

      if (apiData.total > 10) {
        response += `📦 **+${apiData.total - 10}** bản ghi khác...\n\n`;
      }
    } else {
      response += '❌ **Không tìm thấy dữ liệu tiêu thụ.**\n\n';
    }

    response += `━━━━━━━━━━━━━━━━━━━━━━━━━━`;

    if (apiData.isMock) {
      response += '\n\n⚠️ *Dữ liệu mô phỏng (Demo mode)*';
    }

    return response;
  }

  /**
   * Tạo template response cho warning data
   */
  generateWarningResponse(apiData) {
    // Nếu cần nhập dữ liệu hoặc không tìm thấy
    if (apiData.needsInput || (apiData.message && !apiData.warnings.length)) {
      return apiData.message;
    }

    let response = `⚠️ **CẢNH BÁO THIẾT BỊ**\n`;
    response += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    // Nếu là tổng quát (không có mã khách hàng)
    if (apiData.type === 'warning_summary') {
      response += `📅 **Ngày:** ${apiData.date}\n`;
      response += `📈 **Tổng cảnh báo:** **${apiData.total}** cảnh báo\n`;
      response += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

      if (apiData.statistics && Object.keys(apiData.statistics).length > 0) {
        const stats = apiData.statistics;

        response += `📊 **Chi tiết theo loại:**\n\n`;

        const warningTypes = [
          { label: 'Cảnh báo tấn công tháo rời', value: stats.demolition, icon: '🔴' },
          { label: 'Cảnh báo tấn công từ tính', value: stats.magnetic, icon: '🟣' },
          { label: 'Cảnh báo kẹt công tơ', value: stats.stop_status, icon: '🟠' },
          { label: 'Cảnh báo dòng chảy ngược', value: stats.reverse_flow, icon: '🟢' },
          { label: 'Cảnh báo dòng chảy vượt ngưỡng', value: stats.over_flow, icon: '🔵' },
          { label: 'Cảnh báo tiêu thụ bất thường', value: stats.abnormal_consumption, icon: '🟡' }
        ];

        warningTypes.forEach((type) => {
          response += `${type.icon} *${type.label}:* **${type.value}**\n`;
        });

        response += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      } else if (apiData.total === 0) {
        response += '✅ **Không có cảnh báo nào hôm nay.**\n\n';
        response += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      }

      response += `\n💡 *Để xem cảnh báo của khách hàng cụ thể, gõ:* "cảnh báo [mã KH]"`;
      return response;
    }

    // Nếu có mã khách hàng (logic cũ)
    response += `🔍 **Mã khách hàng:** **${apiData.customerId}**\n`;
    response += `📈 **Tổng cảnh báo:** **${apiData.total}** cảnh báo\n`;
    response += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    if (apiData.warnings.length > 0) {
      // Phân loại cảnh báo theo trạng thái
      const unresolved = apiData.warnings.filter((w) => w.status === 0);
      const resolved = apiData.warnings.filter((w) => w.status === 1);

      if (unresolved.length > 0) {
        response += `🔴 **Chưa xử lý** (**${unresolved.length}**)\n\n`;
        unresolved.slice(0, 5).forEach((warning, index) => {
          response += `**${index + 1}. ${warning.name}**\n`;
          response += `   • Khách hàng: **${warning.customer_name}** (Mã: **${warning.customer_id}**)\n`;
          response += `   • Số sêri: **${warning.serial_no}**\n`;
          response += `   • Nhà máy: ${warning.branch || 'N/A'}\n`;
          if (warning.date_check) {
            const dateCheck = new Date(warning.date_check);
            response += `   • Ngày xảy ra: **${dateCheck.toLocaleDateString('vi-VN')}**\n`;
          }
          if (warning.created_at) {
            const createdAt = new Date(warning.created_at);
            response += `   • Ngày thông báo: ${createdAt.toLocaleDateString(
              'vi-VN'
            )} ${createdAt.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}\n`;
          }
          response += `\n`;
        });

        if (unresolved.length > 5) {
          response += `📦 **+${unresolved.length - 5}** cảnh báo chưa xử lý khác...\n\n`;
        }
        response += `───────────────────────────\n\n`;
      }

      if (resolved.length > 0) {
        response += `🟢 **Đã xử lý** (**${resolved.length}**)\n\n`;
        resolved.slice(0, 3).forEach((warning, index) => {
          response += `**${index + 1}. ${warning.name}**\n`;
          response += `   • Khách hàng: ${warning.customer_name} (Mã: **${warning.customer_id}**)\n`;
          response += `   • Số sêri: **${warning.serial_no}**\n`;
          if (warning.date_check) {
            const dateCheck = new Date(warning.date_check);
            response += `   • Ngày xảy ra: ${dateCheck.toLocaleDateString('vi-VN')}\n`;
          }
          response += `\n`;
        });

        if (resolved.length > 3) {
          response += `📦 **+${resolved.length - 3}** cảnh báo đã xử lý khác...\n\n`;
        }
      }
    } else {
      response += '❌ **Không tìm thấy cảnh báo nào.**\n\n';
    }

    response += `━━━━━━━━━━━━━━━━━━━━━━━━━━`;

    if (apiData.isMock) {
      response += '\n\n⚠️ *Dữ liệu mô phỏng (Demo mode)*';
    }

    return response;
  }

  /**
   * Lấy dữ liệu từ API dựa trên intent
   */
  async fetchRelevantData(intent, userMessage) {
    try {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();

      // Phát hiện ngày từ câu hỏi
      const detectedDate = this.detectDateFromMessage(userMessage);

      switch (intent.intent) {
        case 'MONTHLY_CONSUMPTION_QUERY':
          // Lấy dữ liệu tiêu thụ theo tháng/kỳ
          return await this.getMonthlyConsumptionData(userMessage);

        case 'CONSUMPTION_QUERY':
          // Kiểm tra xem có hỏi về Nhà máy không
          if (
            userMessage.toLowerCase().includes('Nhà máy') ||
            userMessage.toLowerCase().includes('branch')
          ) {
            return await this.getBranchConsumptionByDay(detectedDate, userMessage);
          }
          // Lấy dữ liệu giám sát tiêu thụ thông thường
          return await this.getConsumptionData(currentMonth, currentYear);

        case 'STATISTICS_QUERY':
          // Lấy thống kê theo tháng
          return await this.getStatisticsData(currentMonth, currentYear);

        case 'CUSTOMER_QUERY':
          // Lấy danh sách khách hàng
          return await this.getCustomerData(intent.entity);

        case 'BRANCH_QUERY':
          // Lấy dữ liệu theo Nhà máy theo ngày
          return await this.getBranchConsumptionByDay(detectedDate, userMessage);

        case 'WARNING_QUERY':
          // Lấy dữ liệu cảnh báo theo mã khách hàng
          return await this.getWarningData(userMessage);

        default:
          return null;
      }
    } catch (error) {
      console.error('Fetch data error:', error);
      return null;
    }
  }

  /**
   * Lấy dữ liệu tiêu thụ
   */
  async getConsumptionData(month, year) {
    try {
      const [monitoring, sumMonth, sumDay] = await Promise.all([
        apiGetConsumptionMonitoring({ month, year, page: 1, size: 10 }),
        apiGetSumConsumptionByMonth({ month, year }),
        apiGetSumConsumptionByDay({
          startDate: `${year}-${String(month).padStart(2, '0')}-01`,
          endDate: new Date(year, month, 0).toISOString().split('T')[0]
        })
      ]);

      return {
        type: 'consumption',
        monitoring: monitoring.data?.data?.items || [],
        totalMonth: sumMonth.data?.data || {},
        totalDay: sumDay.data?.data || {},
        summary: {
          totalCustomers: monitoring.data?.data?.total || 0,
          period: `${month}/${year}`
        }
      };
    } catch (error) {
      console.error('Get consumption data error:', error);
      return null;
    }
  }

  /**
   * Lấy dữ liệu thống kê
   */
  async getStatisticsData(month, year) {
    try {
      const [monthStats, dayStats] = await Promise.all([
        apiGetStatisticsDataByMonth({ month, year, page: 1, size: 10 }),
        apiGetStatisticsCustomerByDay({
          startDate: `${year}-${String(month).padStart(2, '0')}-01`,
          endDate: new Date(year, month, 0).toISOString().split('T')[0],
          page: 1,
          size: 10
        })
      ]);

      return {
        type: 'statistics',
        monthData: monthStats.data?.data || {},
        dayData: dayStats.data?.data || {},
        summary: {
          period: `Tháng ${month}/${year}`,
          totalRecords: monthStats.data?.data?.total || 0
        }
      };
    } catch (error) {
      console.error('Get statistics data error:', error);
      return null;
    }
  }

  /**
   * Lấy dữ liệu khách hàng
   */
  async getCustomerData(customerName = null) {
    try {
      const params = {
        page: 1,
        size: 10
      };

      if (customerName) {
        params.keyWord = customerName; // Param đúng theo API thực tế
      }

      const response = await apiGetAllCustomer(params);

      // Kiểm tra có dữ liệu không (response.data.data là mảng trực tiếp)
      if (
        response.data?.data &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        return {
          type: 'customer',
          customers: response.data.data, // Mảng trực tiếp, không có .items
          total: response.data?.meta?.total || response.data.data.length, // Total nằm ở meta.total
          searchTerm: customerName,
          isMock: false
        };
      }

      // Nếu không có dữ liệu thực, trả về mock data
      console.warn('⚠️ API không trả về dữ liệu, sử dụng mock data');
      return this.getMockCustomerData(customerName);
    } catch (error) {
      console.error('Get customer data error:', error);
      // Trả về mock data khi API lỗi
      return this.getMockCustomerData(customerName);
    }
  }

  /**
   * Lấy mock customer data cho demo
   */
  getMockCustomerData(searchTerm = null) {
    const mockCustomers = [
      {
        id: 7831,
        code: '6678876',
        name: 'Công ty Cổ phần Đầu tư Pacific',
        phone: '0918146611',
        branch: 'Nhà máy cấp nước Hải Phòng 3',
        manage_group: 'Hồng Bàng - Thủy Nguyên',
        address: 'Khu đô thị mới 2A, Phường Sở Dầu, Quận Hồng Bàng',
        serial_number: '11W706300',
        device_name: 'Helix H4000',
        install_time: '12/07/2023',
        size: 'D100',
        type: 'Tuốc bin',
        status: 'IN_USE'
      },
      {
        id: 7830,
        code: '6661504',
        name: 'Nhà máy Công ty TNHH MM Mega Market (Việt Nam) tại Hải Phòng',
        phone: '0904253101',
        branch: 'Nhà máy cấp nước Hải Phòng 3',
        manage_group: 'Hồng Bàng - Thủy Nguyên',
        address: 'H.Bàng',
        serial_number: '2045001025',
        device_name: 'Italia Maddalena(Class2)-WP',
        install_time: '07/09/2021',
        size: 'D65',
        type: 'Tuốc bin - WPD',
        status: 'IN_USE'
      },
      {
        id: 7829,
        code: '6791269',
        name: 'Công ty Cổ phần Thương mại Dịch vụ Tự Long',
        phone: '0896693989',
        branch: 'Nhà máy cấp nước Trung tâm',
        manage_group: 'Hồng Bàng - Thủy Nguyên',
        address: 'Số 55 Quốc lộ 5, Phường Sở Dầu, Quận Hồng Bàng',
        serial_number: 'D130F126375',
        device_name: 'WOLTEX',
        install_time: '05/05/2024',
        size: 'D50',
        type: 'Tuốc bin',
        status: 'IN_USE'
      },
      {
        id: 7828,
        code: '6732026',
        name: 'Nhà máy Công ty TNHH Một thành viên Điện lực Hải Phòng',
        phone: '0936227774',
        branch: 'Nhà máy cấp nước Trung tâm',
        manage_group: 'Hồng Bàng - Thủy Nguyên',
        address: 'Khu Liên Cơ An Lạc, đường 5 mới, Phường Hùng Vương',
        serial_number: '11W719512',
        device_name: 'Helix H4000',
        install_time: '10/09/2020',
        size: 'D100',
        type: 'Tuốc bin - WPD',
        status: 'IN_USE'
      },
      {
        id: 7827,
        code: '6634991',
        name: 'Bệnh viện Phụ sản Hải Phòng',
        phone: '0903212221',
        branch: 'Nhà máy cấp nước Trung tâm',
        manage_group: 'Hồng Bàng - Thủy Nguyên',
        address: 'Số 40 đường 5 mới, phường Hùng Vương, quận Hồng Bàng',
        serial_number: '78952865',
        device_name: 'Ba Lan',
        install_time: '03/09/2024',
        size: 'D80',
        type: 'Tuốc bin - WPD',
        status: 'IN_USE'
      }
    ];

    // Filter nếu có search term
    let filteredCustomers = mockCustomers;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredCustomers = mockCustomers.filter(
        (c) =>
          c.name.toLowerCase().includes(searchLower) ||
          c.code.includes(searchLower) ||
          c.phone.includes(searchLower)
      );
    }

    return {
      type: 'customer',
      customers: filteredCustomers,
      total: filteredCustomers.length,
      searchTerm: searchTerm,
      isMock: true
    };
  }

  /**
   * Lấy dữ liệu Nhà máy
   */
  async getBranchData() {
    // Giả sử có API lấy danh sách Nhà máy, nếu không có thì trả về null
    // const response = await apiGetBranches();
    return {
      type: 'branch',
      branches: [],
      message: 'Chức năng tra cứu Nhà máy đang được phát triển'
    };
  }

  /**
   * Lấy dữ liệu cảnh báo theo mã khách hàng
   * @param {string} message - Câu hỏi người dùng
   */
  async getWarningData(message) {
    try {
      // Trích xuất mã khách hàng từ câu hỏi
      const customerId = this.extractCustomerIdFromMessage(message);
      console.log('🔍 getWarningData - Extracted customerId:', customerId);
      console.log('🔍 getWarningData - Original message:', message);

      if (!customerId) {
        console.warn(
          '⚠️ getWarningData - Không tìm thấy mã khách hàng, lấy thống kê cảnh báo hôm nay'
        );

        const now = new Date();
        const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD

        const params = {
          date: dateStr
        };

        console.log(
          '🔍 getWarningData (no customerId) - Calling apiGetWarningStatisticsByType with params:',
          params
        );
        const response = await apiGetWarningStatisticsByType(params);
        console.log('✅ getWarningData (no customerId) - API Response:', response.data);

        if (response.data?.data) {
          const stats = response.data.data;
          const totalWarnings = parseInt(stats.total_warnings || 0);

          return {
            type: 'warning_summary',
            total: totalWarnings,
            date: now.toLocaleDateString('vi-VN'),
            statistics: {
              demolition: parseInt(stats.demolition || 0),
              magnetic: parseInt(stats.magnetic || 0),
              stop_status: parseInt(stats.stop_status || 0),
              reverse_flow: parseInt(stats.reverse_flow || 0),
              over_flow: parseInt(stats.over_flow || 0),
              abnormal_consumption: parseInt(stats.abnormal_consumption || 0)
            },
            needsInput: false,
            isMock: false
          };
        }

        // Fallback nếu API không trả về data
        return {
          type: 'warning_summary',
          total: 0,
          date: now.toLocaleDateString('vi-VN'),
          statistics: {},
          message: '✅ Không có cảnh báo nào hôm nay',
          needsInput: false,
          isMock: false
        };
      }

      const params = {
        page: 1,
        size: 20,
        keyWord: customerId,
        isWarning: 1
      };

      console.log('🔍 getWarningData - Calling apiGetAllWarnings with params:', params);
      const response = await apiGetAllWarnings(params);
      console.log('✅ getWarningData - API Response:', response.data);

      if (
        response.data?.data &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        return {
          type: 'warning',
          warnings: response.data.data,
          total: response.data?.meta?.total || response.data.data.length,
          customerId: customerId,
          isMock: false
        };
      }

      // Không tìm thấy cảnh báo
      return {
        type: 'warning',
        warnings: [],
        total: 0,
        customerId: customerId,
        message: `❌ Không tìm thấy cảnh báo nào cho khách hàng **${customerId}**`,
        isMock: false
      };
    } catch (error) {
      console.error('Get warning data error:', error);
      return {
        type: 'warning',
        warnings: [],
        total: 0,
        message: '❌ Có lỗi khi tra cứu cảnh báo. Vui lòng thử lại sau.',
        error: true
      };
    }
  }

  /**
   * Trích xuất mã khách hàng từ tin nhắn
   * @param {string} message - Tin nhắn người dùng
   * @returns {string|null} - Mã khách hàng hoặc null
   */
  extractCustomerIdFromMessage(message) {
    // Tìm các pattern phổ biến:
    // "mã khách hàng 6678876"
    // "mã 6678876"
    // "khách hàng 6678876"
    // "cảnh báo 6678876"
    // "6678876" (mã khách hàng thuần)
    const patterns = [
      /m[aã]\s*(?:kh[aá]ch\s*h[aà]ng)?\s*[:-]?\s*(\d{6,10})/i,
      /kh[aá]ch\s*h[aà]ng\s*[:-]?\s*(\d{6,10})/i,
      /c[aả]nh\s*b[aá]o\s*(?:cho)?\s*[:-]?\s*(\d{6,10})/i,
      /tra\s*c[ứu]?\s*[:-]?\s*(\d{6,10})/i,
      /^\s*(\d{6,10})\s*$/ // Chỉ có mã số thuần
    ];

    console.log('🔍 extractCustomerIdFromMessage - Input message:', message);

    for (let i = 0; i < patterns.length; i++) {
      const pattern = patterns[i];
      const match = message.match(pattern);
      if (match && match[1]) {
        console.log(`✅ extractCustomerIdFromMessage - Matched pattern ${i}: ${match[1]}`);
        return match[1];
      }
    }

    console.warn('⚠️ extractCustomerIdFromMessage - No match found');
    return null;
  }

  /**
   * Lấy dữ liệu tiêu thụ theo tháng/kỳ của khách hàng
   * @param {string} message - Câu hỏi người dùng
   */
  async getMonthlyConsumptionData(message) {
    try {
      // Phát hiện tháng từ câu hỏi
      const detectedMonth = this.detectMonthFromMessage(message);

      // Phát hiện Nhà máy từ câu hỏi
      const detectedBranch = this.detectBranchFromMessage(message);

      // Phát hiện keyword tìm kiếm (mã KH, tên KH, IMEI...)
      const keyword = this.extractSearchKeyword(message);

      // Kiểm tra xem có đủ thông tin để tra cứu không
      if (!keyword && !detectedBranch) {
        return {
          type: 'monthly_consumption_no_input',
          period: detectedMonth,
          message:
            '⚠️ **Thiếu thông tin tra cứu**\n' +
            '━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n' +
            '📋 **Yêu cầu:** Vui lòng cung cấp một trong các thông tin sau:\n\n' +
            '**1️⃣ Thông tin khách hàng:**\n' +
            '   • Mã khách hàng (VD: 6678876)\n' +
            '   • Tên khách hàng (VD: Pacific)\n\n' +
            '**2️⃣ Nhà máy cấp nước:**\n' +
            '   • Trung tâm\n' +
            '   • Hải Phòng 3, 4, 5, 7, 8\n' +
            '   • Vĩnh Bảo\n' +
            '   • Cát Bà\n\n' +
            '━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
            '💡 **Ví dụ cách hỏi:**\n\n' +
            '✅ "Tiêu thụ tháng này của mã 6678876"\n' +
            '✅ "Tiêu thụ tháng 01/2026 của khách hàng Pacific"\n' +
            '✅ "Chỉ số tháng này của Nhà máy Trung tâm"\n' +
            '✅ "Tiêu thụ HP3 tháng 12/2025"'
        };
      }

      const params = {
        page: 1,
        size: 10,
        period: detectedMonth, // Format: YYYY-MM-DD
        keyWord: keyword || undefined,
        branch_code: detectedBranch?.code || undefined
      };

      const response = await apiGetStatisticsDataByMonthV3(params);

      // Kiểm tra có dữ liệu không
      if (
        response.data?.data &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        return {
          type: 'monthly_consumption',
          records: response.data.data,
          total: response.data?.meta?.total || response.data.data.length,
          period: detectedMonth,
          searchTerm: keyword,
          branchInfo: detectedBranch,
          isMock: false
        };
      }

      // Nếu không có dữ liệu thực, trả về mock data
      console.warn('⚠️ API không trả về dữ liệu tiêu thụ theo tháng, sử dụng mock data');
      return this.getMockMonthlyConsumptionData(detectedMonth, keyword, detectedBranch);
    } catch (error) {
      console.error('Get monthly consumption data error:', error);
      // Trả về mock data khi API lỗi
      return this.getMockMonthlyConsumptionData(
        this.detectMonthFromMessage(message),
        this.extractSearchKeyword(message),
        this.detectBranchFromMessage(message)
      );
    }
  }

  /**
   * Lấy mock monthly consumption data cho demo
   */
  getMockMonthlyConsumptionData(period, searchTerm = null, branchInfo = null) {
    const mockRecords = [
      {
        customer_id: '6678876',
        customer_name: 'Công ty Cổ phần Đầu tư Pacific',
        branch: 'HP3',
        manage_group: 'Hồng Bàng - Thủy Nguyên',
        parent_device: 'Ống Trục',
        route: '5318',
        size: 'D100',
        period: period,
        first_day: '01/01/2026',
        last_day: '31/01/2026',
        start_reading: 125678.456,
        end_reading: 128934.123,
        consumption: 3255.667,
        adjusted_consumption_value: 0,
        final_consumption: 3255.667,
        status: 2
      },
      {
        customer_id: '6661504',
        customer_name: 'Nhà máy Công ty TNHH MM Mega Market',
        branch: 'HP3',
        manage_group: 'Hồng Bàng - Thủy Nguyên',
        parent_device: 'Ống Trục',
        route: '5318',
        size: 'D65',
        period: period,
        first_day: '01/01/2026',
        last_day: '31/01/2026',
        start_reading: 98234.567,
        end_reading: 100123.89,
        consumption: 1889.323,
        adjusted_consumption_value: 0,
        final_consumption: 1889.323,
        status: 2
      }
    ];

    // Filter nếu có search term
    let filteredRecords = mockRecords;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredRecords = mockRecords.filter(
        (r) =>
          r.customer_id.includes(searchLower) || r.customer_name.toLowerCase().includes(searchLower)
      );
    }

    return {
      type: 'monthly_consumption',
      records: filteredRecords,
      total: filteredRecords.length,
      period: period,
      searchTerm: searchTerm,
      branchInfo: branchInfo,
      isMock: true
    };
  }

  /**
   * Phát hiện tháng từ câu hỏi
   * @param {string} message - Câu hỏi người dùng
   * @returns {string} - Tháng theo format YYYY-MM-DD (ngày đầu tháng)
   */
  detectMonthFromMessage(message) {
    const today = new Date();
    const lowerMessage = message.toLowerCase();

    // Tháng này
    if (lowerMessage.includes('tháng này') || lowerMessage.includes('this month')) {
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
    }

    // Tháng trước
    if (lowerMessage.includes('tháng trước') || lowerMessage.includes('last month')) {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01`;
    }

    // Tháng cụ thể: "tháng 12/2025", "tháng 12 năm 2025", "12/2025"
    const monthYearPattern = /(\d{1,2})[/-](\d{4})/;
    const monthYearMatch = message.match(monthYearPattern);
    if (monthYearMatch) {
      const month = monthYearMatch[1].padStart(2, '0');
      const year = monthYearMatch[2];
      return `${year}-${month}-01`;
    }

    // Tháng với năm: "tháng 12 năm 2025"
    const monthTextPattern = /tháng\s*(\d{1,2})\s*năm\s*(\d{4})/i;
    const monthTextMatch = message.match(monthTextPattern);
    if (monthTextMatch) {
      const month = monthTextMatch[1].padStart(2, '0');
      const year = monthTextMatch[2];
      return `${year}-${month}-01`;
    }

    // Mặc định: tháng hiện tại
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  }

  /**
   * Phát hiện Nhà máy từ câu hỏi
   * @param {string} message - Câu hỏi người dùng
   * @returns {Object|null} - Thông tin Nhà máy { name, code } hoặc null
   */
  detectBranchFromMessage(message) {
    const lowerMessage = message.toLowerCase();

    // Tìm Nhà máy dựa trên tên và aliases
    for (const branch of this.branches) {
      // Kiểm tra tên đầy đủ
      if (lowerMessage.includes(branch.name.toLowerCase())) {
        return { name: branch.name, code: branch.code };
      }

      // Kiểm tra aliases
      for (const alias of branch.aliases) {
        if (lowerMessage.includes(alias)) {
          return { name: branch.name, code: branch.code };
        }
      }
    }

    return null;
  }

  /**
   * Trích xuất từ khóa tìm kiếm từ câu hỏi
   */
  extractSearchKeyword(message) {
    const lowerMessage = message.toLowerCase();

    // Tìm mã khách hàng (số)
    const codePattern = /\b\d{6,8}\b/;
    const codeMatch = message.match(codePattern);
    if (codeMatch) {
      return codeMatch[0];
    }

    // Tìm từ khóa sau "khách hàng", "của", "cho"
    const keywordPatterns = [
      /(?:khách hàng|của|cho)\s+([a-zA-ZÀ-ỹ0-9\s]+?)(?:\s+tháng|\s+trong|\s+kỳ|$)/i,
      /tìm\s+([a-zA-ZÀ-ỹ0-9\s]+?)(?:\s+tháng|\s+trong|\s+kỳ|$)/i
    ];

    for (const pattern of keywordPatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    return null;
  }

  /**
   * Phát hiện ngày từ câu hỏi
   * @param {string} message - Câu hỏi người dùng
   * @returns {string} - Ngày theo format YYYY-MM-DD
   */
  detectDateFromMessage(message) {
    const today = new Date();
    const lowerMessage = message.toLowerCase();

    // Hôm nay
    if (lowerMessage.includes('hôm nay') || lowerMessage.includes('today')) {
      return this.formatDate(today);
    }

    // Hôm qua
    if (lowerMessage.includes('hôm qua') || lowerMessage.includes('yesterday')) {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return this.formatDate(yesterday);
    }

    // Hôm kia
    if (lowerMessage.includes('hôm kia')) {
      const dayBeforeYesterday = new Date(today);
      dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
      return this.formatDate(dayBeforeYesterday);
    }

    // Ngày cụ thể: dd/mm/yyyy hoặc dd-mm-yyyy
    const datePattern = /(\d{1,2})[/-](\d{1,2})[/-](\d{4})/;
    const match = message.match(datePattern);
    if (match) {
      const day = match[1].padStart(2, '0');
      const month = match[2].padStart(2, '0');
      const year = match[3];
      return `${year}-${month}-${day}`;
    }

    // Mặc định là hôm nay
    return this.formatDate(today);
  }

  /**
   * Format date thành YYYY-MM-DD
   */
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Lấy dữ liệu tiêu thụ theo Nhà máy theo ngày
   * @param {string} date - Ngày cần tra cứu (YYYY-MM-DD)
   * @param {string} message - Câu hỏi để detect Nhà máy cụ thể
   */
  async getBranchConsumptionByDay(date, message = '') {
    try {
      console.log('Fetching branch consumption for date:', date);
      const response = await apiGetConsumptionByBranchByDay({ date });
      console.log('API Response:', response);

      // Flexible data access
      const data = response.data?.data || response.data;

      if (!data) {
        console.warn('No data returned from API');
        // Trả về mock data để demo nếu API thật chưa có dữ liệu hoặc lỗi
        // Comment phần này lại khi API đã hoạt động ổn định
        return this.getMockBranchData(date, message);
      }

      // Phát hiện Nhà máy được hỏi (nếu có)
      let requestedBranch = null;
      const lowerMessage = message.toLowerCase();

      // Map các cách gọi tên Nhà máy
      const branchKeywords = {
        'trung tâm': 'trung tâm',
        'hp 3': 'hải phòng 3',
        'hp 4': 'hải phòng 4',
        'hp 5': 'hải phòng 5',
        'hp 7': 'hải phòng 7',
        'hp 8': 'hải phòng 8',
        'cát bà': 'cát bà',
        'vĩnh bảo': 'vĩnh bảo'
      };

      for (const [keyword, branchName] of Object.entries(branchKeywords)) {
        if (lowerMessage.includes(keyword)) {
          requestedBranch = data.branches.find((b) =>
            b.branch_name.toLowerCase().includes(branchName)
          );
          break;
        }
      }

      return {
        type: 'branch_consumption',
        date: data.date || date,
        branches: data.branches || [],
        totalConsumption: data.total_consumption || data.totalConsumption || 0,
        requestedBranch: requestedBranch,
        hasSpecificBranch: !!requestedBranch
      };
    } catch (error) {
      console.error('Get branch consumption by day error:', error);
      return null;
    }
  }

  /**
   * Mock data cho demo khi API chưa có dữ liệu
   */
  getMockBranchData(date, message) {
    const branches = [
      { branch_code: 'CN001', branch_name: 'Cấp nước Trung tâm', consumption: 8543210 },
      { branch_code: 'CN002', branch_name: 'Nhà máy Hải Phòng 3', consumption: 4231567 },
      { branch_code: 'CN003', branch_name: 'Nhà máy Hải Phòng 4', consumption: 3890123 },
      { branch_code: 'CN004', branch_name: 'Nhà máy Cát Bà', consumption: 1234567 }
    ];

    const totalConsumption = branches.reduce((sum, b) => sum + b.consumption, 0);

    // Detect requested branch similar to main logic
    let requestedBranch = null;
    const lowerMessage = message.toLowerCase();
    const branchKeywords = {
      'trung tâm': 'trung tâm',
      'hp 3': 'hải phòng 3',
      'cát bà': 'cát bà'
    };

    for (const [keyword, branchName] of Object.entries(branchKeywords)) {
      if (lowerMessage.includes(keyword)) {
        requestedBranch = branches.find((b) => b.branch_name.toLowerCase().includes(branchName));
        break;
      }
    }

    return {
      type: 'branch_consumption',
      date: date,
      branches: branches,
      totalConsumption: totalConsumption,
      requestedBranch: requestedBranch,
      hasSpecificBranch: !!requestedBranch,
      isMock: true
    };
  }

  /**
   * Xây dựng context từ dữ liệu API
   */
  buildContextFromData(apiData, intent) {
    if (!apiData) return '';

    let context = '\nDữ liệu hệ thống hiện tại:\n';
    if (apiData.isMock) {
      context += '(⚠️ Lưu ý: Đây là dữ liệu giả lập do API chưa trả về kết quả)\n';
    }

    switch (apiData.type) {
      case 'consumption':
        context += `- Tổng số khách hàng: ${apiData.summary.totalCustomers}\n`;
        context += `- Kỳ: ${apiData.summary.period}\n`;
        if (apiData.monitoring.length > 0) {
          context += `- Một số khách hàng tiêu biểu:\n`;
          apiData.monitoring.slice(0, 5).forEach((item, index) => {
            context += `  ${index + 1}. ${item.customerName || 'N/A'}: ${
              item.consumption || 0
            } m³\n`;
          });
        }
        break;

      case 'statistics':
        context += `- Kỳ thống kê: ${apiData.summary.period}\n`;
        context += `- Tổng số bản ghi: ${apiData.summary.totalRecords}\n`;
        break;

      case 'customer':
        context += `- Tổng số khách hàng: ${apiData.total}\n`;
        if (apiData.searchTerm) {
          context += `- Từ khóa tìm kiếm: "${apiData.searchTerm}"\n`;
        }
        if (apiData.customers.length > 0) {
          context += `- Danh sách khách hàng (hiển thị tối đa 10):\n`;
          apiData.customers.slice(0, 10).forEach((customer, index) => {
            context += `\n  ${index + 1}. ${customer.name || 'N/A'}\n`;
            context += `     - Mã KH: ${customer.code || 'N/A'}\n`;
            context += `     - Điện thoại: ${customer.phone || 'N/A'}\n`;
            context += `     - Nhà máy: ${customer.branch || 'N/A'}\n`;
            context += `     - Địa chỉ: ${customer.address || 'N/A'}\n`;
            context += `     - Thiết bị: ${customer.device_name || 'N/A'} (${
              customer.size || 'N/A'
            })\n`;
            context += `     - Số seri: ${customer.serial_number || 'N/A'}\n`;
            context += `     - Trạng thái: ${customer.status || 'N/A'}\n`;
          });
        } else {
          context += '- Không tìm thấy khách hàng nào.\n';
        }
        break;

      case 'branch':
        context += apiData.message;
        break;

      case 'branch_consumption':
        context += `⚡ LOẠI DỮLIỆU: TIÊU THỤ THEO NGÀY CỦA Nhà máy (hàng ngày, không phải kỳ cước)\n`;
        context += `- Ngày: ${apiData.date}\n`;
        context += `- Tổng tiêu thụ toàn hệ thống: ${this.formatConsumptionVN(
          apiData.totalConsumption
        )} m³\n`;

        if (apiData.hasSpecificBranch && apiData.requestedBranch) {
          // Nếu hỏi về Nhà máy cụ thể
          const branch = apiData.requestedBranch;
          context += `\nNhà máy được hỏi: ${branch.branch_name}\n`;
          context += `- Mã Nhà máy: ${branch.branch_code}\n`;
          context += `- Tiêu thụ: ${this.formatConsumptionVN(branch.consumption)} m³\n`;
          context += `- Chiếm: ${((branch.consumption / apiData.totalConsumption) * 100).toFixed(
            2
          )}% tổng tiêu thụ\n`;
        } else {
          // Hiển thị tất cả Nhà máy
          context += `\nDanh sách các Nhà máy:\n`;
          apiData.branches
            .sort((a, b) => b.consumption - a.consumption)
            .forEach((branch, index) => {
              context += `${index + 1}. ${branch.branch_name}: ${this.formatConsumptionVN(
                branch.consumption
              )} m³\n`;
            });
        }
        break;

      case 'monthly_consumption': {
        const periodDate = new Date(apiData.period);
        const periodText = `${String(periodDate.getMonth() + 1).padStart(
          2,
          '0'
        )}/${periodDate.getFullYear()}`;

        context += `⚡ LOẠI DỮLIỆU: TIÊU THỤ THEO KỲ CƯỚC/THÁNG CỦA KHÁCH HÀNG (kỳ cước, không phải theo ngày)\n`;
        context += `- Kỳ hóa đơn: ${periodText}\n`;
        context += `- Tổng số bản ghi: ${apiData.total}\n`;
        if (apiData.branchInfo) {
          context += `- Nhà máy: ${apiData.branchInfo.name} (Mã: ${apiData.branchInfo.code})\n`;
        }
        if (apiData.searchTerm) {
          context += `- Từ khóa tìm kiếm: "${apiData.searchTerm}"\n`;
        }

        if (apiData.records.length > 0) {
          context += `\n- Danh sách tiêu thụ (hiển thị tối đa 10):\n`;
          apiData.records.slice(0, 10).forEach((record, index) => {
            context += `\n  ${index + 1}. ${record.customer_name} (Mã: ${record.customer_id})\n`;
            context += `     - Nhà máy: ${record.branch}\n`;
            context += `     - Cỡ ĐH: ${record.size}\n`;
            context += `     - Từ ngày: ${record.first_day}\n`;
            context += `     - Đến ngày: ${record.last_day}\n`;
            context += `     - Chỉ số cũ: ${this.formatNumber(record.start_reading || 0)} m³\n`;
            context += `     - Chỉ số mới: ${this.formatNumber(record.end_reading || 0)} m³\n`;
            context += `     - Tiêu thụ hệ thống: ${this.formatNumber(
              record.consumption || 0
            )} m³\n`;
            if (record.adjusted_consumption_value) {
              context += `     - Điều chỉnh: ${this.formatNumber(
                record.adjusted_consumption_value
              )} m³\n`;
            }
            context += `     - Tiêu thụ tính cước: ${this.formatNumber(
              record.final_consumption || 0
            )} m³\n`;
            let statusText = 'Đã tính toán';
            if (record.status === 0) statusText = 'Chưa gán';
            else if (record.status === 1) statusText = 'Chưa đủ dữ liệu';
            else if (record.status === 3) statusText = 'Đọc thủ công';
            context += `     - Trạng thái: ${statusText}\n`;
          });
        } else {
          context += '- Không tìm thấy dữ liệu tiêu thụ nào.\n';
        }
        break;
      }

      case 'monthly_consumption_no_input': {
        context = `Người dùng đang hỏi về tiêu thụ theo tháng/kỳ nhưng chưa cung cấp thông tin cần thiết.
Hãy giải thích rằng họ cần cung cấp:
- Mã khách hàng hoặc tên khách hàng, HOẶC
- Tên Nhà máy để tra cứu

Danh sách Nhà máy có sẵn:
${this.branches.map((b) => `- ${b.name} (Mã: ${b.code})`).join('\n')}

Hãy đưa ra ví dụ cụ thể về cách hỏi đúng.`;
        break;
      }

      default:
        context = '';
    }

    return context;
  }

  /**
   * Format số với dấu phẩy phân cách (kiểu Mỹ: 1,234,567)
   */
  formatNumber(num) {
    return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0';
  }

  /**
   * Format số kiểu Việt Nam cho consumption
   * Ví dụ: 2319415 lít -> 2.319,415 m³
   * @param {number} liters - Số lít
   * @returns {string} - Số m³ đã format
   */
  formatConsumptionVN(liters) {
    if (!liters && liters !== 0) return '0,000';

    // Chuyển lít sang m³ (chia 1000)
    const m3 = liters / 1000;

    // Format theo kiểu Việt Nam: dấu chấm ngăn nghìn, dấu phẩy thập phân
    return formatCurrency(m3, 3);
  }

  /**
   * Format và làm đẹp AI response
   * - Giảm số lượng ** dư thừa
   * - Cải thiện structure
   * - Format bullets và numbers
   */
  formatAIResponse(text) {
    if (!text) return text;

    let formatted = text;

    // 1. Rút ngắn tên Nhà máy (giữ nguyên **)
    formatted = formatted.replace(/Nhà máy cấp nước (Hải Phòng \d+)/gi, 'HP$1');
    formatted = formatted.replace(/Nhà máy cấp nước (Trung tâm|Vĩnh Bảo|Cát Bà)/gi, '$1');

    // 2. Loại bỏ ** CHỈ từ một số từ lặp lại quá nhiều
    // KHÔNG loại bỏ ** từ tên, địa danh, số liệu
    formatted = formatted.replace(/\*\*(tiêu thụ nước)\*\*/gi, 'tiêu thụ nước');
    formatted = formatted.replace(/\*\*(của các Nhà máy|của họ)\*\*/gi, '$1');

    // 3. Thay * thành • cho bullets (trừ markdown **bold**)
    formatted = formatted.replace(/^\s*\*\s+(?!\*)/gm, '• ');

    // 4. Format danh sách số thứ tự - loại ** ngay sau số
    formatted = formatted.replace(/^(\s*\d+\.)\s*\*\*/gm, '$1 ');

    // 5. Thêm spacing trước emoji headers
    formatted = formatted.replace(/\n([📊💧🏢📈📍🔍📋📅🎯💡⚡])/gu, '\n\n$1');

    // 6. Format sections với separators
    formatted = formatted.replace(
      /^(Dựa trên dữ liệu|So sánh|Nhận xét|Phân tích)/gim,
      '\n━━━━━━━━━━━━\n*$1*'
    );

    // 7. Clean up spacing
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
    formatted = formatted.replace(/^\s+/gm, (match) => {
      return match.replace(/ {2,}/g, '  '); // Max 2 spaces indent
    });

    // 8. Trim
    formatted = formatted.trim();

    return formatted;
  }

  /**
   * Trả về response fallback khi có lỗi
   */
  getFallbackResponse(error) {
    const errorMessage = error?.message || '';

    if (errorMessage.includes('API')) {
      return 'Xin lỗi, hiện tại tôi không thể kết nối với hệ thống. Vui lòng kiểm tra cấu hình API key hoặc thử lại sau.';
    }

    if (errorMessage.includes('network')) {
      return 'Có lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet của bạn.';
    }

    return 'Xin lỗi, tôi không thể xử lý yêu cầu ngay bây giờ. Vui lòng thử lại hoặc liên hệ quản trị viên.';
  }

  /**
   * Lấy gợi ý câu hỏi
   */
  getSuggestedQuestions() {
    return [
      'Tiêu thụ theo tháng của khách hàng Pacific',
      'Chỉ số đồng hồ tháng này của mã KH 6678876',
      'Tiêu thụ tháng 01/2026 của Nhà máy HP3',
      'Tiêu thụ hôm nay của Nhà máy Trung tâm',
      'Tìm khách hàng có mã 6678876',
      'Danh sách khách hàng thuộc Nhà máy Hải Phòng 3'
    ];
  }
}

export const chatBotService = new ChatBotService();
